// Sales Order filters (Oct 2026, Product Owner decision superseding
// Product Groups). Every exact sellable product is its own card; Brand and
// Package Size are FILTERS over those cards, never selectors inside one.
//
// The server does all of the work (`GET /catalog/refine`): it filters the
// COMPLETE catalog in the database, pages over individual products with an
// exact `total`, and returns faceted choices — each facet counted with the
// other active filters applied — so a choice that would return nothing is
// not offered. Tenant, catalog and eligibility scope are applied server-side
// exactly as for every other public read.
//
// Values of one filter are OR-ed (Swad or Laxmi), different filters AND-ed
// (Swad AND 4 lb). The category comes from the existing Category row.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const RETAIL_BASE = `${BACKEND_URL}/api/retail/public`;
const EMPTY_FACETS = { categories: [], brands: [], sizes: [] };
const EMPTY_SELECTION = { category: null, brands: [], sizes: [] };

// FastAPI reads a repeated key (`brand=a&brand=b`) as a list; axios would
// send `brand[]=a`, so the query string is built explicitly.
export function buildRefineQuery({
  family, category, brands = [], sizes = [], query, catalogId, linkSlug, limit, offset = 0,
}) {
  const p = new URLSearchParams();
  if (family) p.append("family", family);
  if (category) p.append("category", category);
  brands.forEach((b) => p.append("brand", b));
  sizes.forEach((s) => p.append("size", s.value));
  if (query) p.append("q", query);
  p.append("facets", "true");
  p.append("limit", String(limit));
  p.append("offset", String(offset));
  if (catalogId) p.append("catalog_id", catalogId);
  if (linkSlug) p.append("link_slug", linkSlug);
  return p.toString();
}

export default function useCatalogFilters({
  slug, catalogId, linkSlug, family = null, query = "", enabled = false, pageSize = 40,
}) {
  const [selection, setSelection] = useState(EMPTY_SELECTION);
  const [facets, setFacets] = useState(EMPTY_FACETS);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  const active = !!(selection.category || selection.brands.length || selection.sizes.length);
  // Search text narrows the filtered results; without a filter it is the
  // existing in-page search's job and is not sent.
  const q = active ? (query || "").trim() : "";

  // A new Product Family starts with no filters: a Brand chosen under
  // GROCERY must never silently narrow RESTAURANT.
  useEffect(() => { setSelection(EMPTY_SELECTION); }, [family, catalogId]);

  const fetchPage = useCallback(async (offset, append) => {
    if (!enabled || !slug) return;
    const id = ++requestRef.current;
    setLoading(true);
    try {
      const qs = buildRefineQuery({
        family, category: selection.category, brands: selection.brands, sizes: selection.sizes,
        query: q, catalogId, linkSlug, limit: active ? pageSize : 1, offset,
      });
      const { data } = await axios.get(`${RETAIL_BASE}/${slug}/catalog/refine?${qs}`);
      if (id !== requestRef.current) return;
      setFacets({ ...EMPTY_FACETS, ...(data.facets || {}) });
      setItems((prev) => (append ? [...prev, ...(data.items || [])] : (data.items || [])));
      setTotal(data.total || 0);
      setHasMore(!!data.has_more);
    } catch (_e) {
      if (id !== requestRef.current) return;
      // Filtering is an enhancement: a failure keeps the ordinary menu.
      setFacets(EMPTY_FACETS);
      setItems([]);
      setTotal(0);
      setHasMore(false);
    } finally {
      if (id === requestRef.current) setLoading(false);
    }
  }, [enabled, slug, family, selection, q, catalogId, linkSlug, active, pageSize]);

  useEffect(() => {
    if (!enabled) {
      requestRef.current += 1;
      setFacets(EMPTY_FACETS);
      setItems([]);
      setTotal(0);
      setHasMore(false);
      return;
    }
    fetchPage(0, false);
  }, [enabled, fetchPage]);

  const loadMore = useCallback(() => {
    if (!active || !hasMore || loading) return;
    fetchPage(items.length, true);
  }, [active, hasMore, loading, items.length, fetchPage]);

  const toggleBrand = useCallback((value) => setSelection((s) => ({
    ...s,
    brands: s.brands.includes(value) ? s.brands.filter((b) => b !== value) : [...s.brands, value],
  })), []);
  const toggleSize = useCallback((size) => setSelection((s) => ({
    ...s,
    sizes: s.sizes.some((x) => x.value === size.value)
      ? s.sizes.filter((x) => x.value !== size.value)
      : [...s.sizes, { value: size.value, label: size.label }],
  })), []);
  // Changing Category re-checks the active Brand and Size filters against
  // the NEW category, so a choice that only made sense in the old one never
  // leaves the new category looking empty. Each value is kept only if the
  // new category has products for it; if the kept Brand and Size together
  // still match nothing, the Size is dropped and the Brand kept. Clearing
  // the category only widens the scope, so nothing needs re-checking.
  const categoryRef = useRef(0);
  useEffect(() => { categoryRef.current += 1; }, [family, catalogId]);
  const setCategory = useCallback(async (category) => {
    const next = category || null;
    const { brands, sizes } = selection;
    const id = ++categoryRef.current;
    if (!next || (!brands.length && !sizes.length)) {
      setSelection((s) => ({ ...s, category: next }));
      return;
    }
    const probe = async (b, s) => (await axios.get(`${RETAIL_BASE}/${slug}/catalog/refine?${buildRefineQuery({
      family, category: next, brands: b, sizes: s, catalogId, linkSlug, limit: 1,
    })}`)).data || {};
    let keptBrands = brands;
    let keptSizes = sizes;
    try {
      const scope = await probe([], []);
      const norm = (v) => String(v).trim().toLowerCase();
      const has = (list, value) => (list || []).some((f) => norm(f.value) === norm(value) && f.count > 0);
      keptBrands = brands.filter((b) => has(scope.facets?.brands, b));
      keptSizes = sizes.filter((s) => has(scope.facets?.sizes, s.value));
      if (keptBrands.length && keptSizes.length && !(await probe(keptBrands, keptSizes)).total) keptSizes = [];
    } catch (_e) {
      // Re-checking is an enhancement: on failure keep the filters as they are.
    }
    if (id !== categoryRef.current) return;
    setSelection({ category: next, brands: keptBrands, sizes: keptSizes });
  }, [selection, slug, family, catalogId, linkSlug]);
  const clearAll = useCallback(() => { categoryRef.current += 1; setSelection(EMPTY_SELECTION); }, []);

  // Offered only when the products in this scope actually carry a Brand
  // or a Size — a restaurant menu never shows an empty, useless filter.
  const available = enabled && (facets.brands.length > 0 || facets.sizes.length > 0);

  return useMemo(() => ({
    enabled, available, active, selection, facets, items, total, hasMore, loading,
    toggleBrand, toggleSize, setCategory, clearAll, loadMore,
    activeCount: selection.brands.length + selection.sizes.length + (selection.category ? 1 : 0),
  }), [enabled, available, active, selection, facets, items, total, hasMore, loading,
    toggleBrand, toggleSize, setCategory, clearAll, loadMore]);
}
